'use client';

import Link from 'next/link';
import { useState, useCallback, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Turnstile, type TurnstileRef } from '@/components/turnstile';

const TURNSTILE_CONFIGURED = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const turnstileRef = useRef<TurnstileRef>(null);

  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(undefined);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(undefined);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (TURNSTILE_CONFIGURED && !turnstileToken && process.env.NODE_ENV !== 'development') {
      setError('Bitte warten Sie auf die Sicherheitsprüfung.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken }),
      });

      const data = (await res.json()) as { error?: string };

      if (!res.ok) {
        setError(data.error ?? 'Ein Fehler ist aufgetreten.');
        setTurnstileToken(undefined);
        turnstileRef.current?.reset();
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
      <Card className="w-full max-w-md card-shadow-lg border-border">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold mb-3">
            SB
          </div>
          <CardTitle className="text-xl">Passwort zurücksetzen</CardTitle>
          {!submitted && (
            <p className="text-sm text-muted-foreground mt-1">
              Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres
              Passworts.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {submitted ? (
            <div className="space-y-4 text-center">
              <div className="rounded-md bg-green-50 border border-green-200 px-4 py-4 text-sm text-green-800">
                Falls ein Account mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Link
                zum Zurücksetzen Ihres Passworts geschickt.
              </div>
              <p className="text-sm text-muted-foreground">
                Bitte überprüfen Sie Ihren Posteingang.
              </p>
              <Link
                href="/login"
                className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
              >
                <ArrowLeft className="h-3 w-3" /> Zurück zur Anmeldung
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@firma.de"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <Turnstile
                ref={turnstileRef}
                onToken={handleTurnstileToken}
                onExpire={handleTurnstileExpire}
                onError={handleTurnstileError}
              />

              <Button type="submit" variant="accent" className="w-full" disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Link senden'}
              </Button>

              <div className="text-center">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" /> Zurück zur Anmeldung
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
